import React, { PureComponent } from 'react';
import styled, { css } from 'styled-components';
import PropTypes from 'prop-types';
import { navigate } from 'gatsby';
import Img from 'gatsby-image/withIEPolyfill';
import format from 'date-fns/format';

import { T2B, T2R, T1M } from './Text';
import Button from './Button';
import { colors, animation } from '../styles/constants';
import { getLinkProps } from '../helpers/links';
import { media } from '../styles/constants';

const { shortAnimationDuration, transitionTimingFn } = animation;

const StyledCard = styled.div`
  background-color: ${colors.white};
  color: ${colors.graphite};
  border-radius: 0 0 3rem 0;
  box-shadow: 0 0.2rem 0.6rem 0 rgba(0, 0, 0, 0.2);
  display: flex;
  flex-direction: column;
  max-width: 60rem;
  width: 100%;

  will-change: transform, opacity, box-shadow, color;
  transition-property: transform, opacity, box-shadow, color;
  transition-duration: ${shortAnimationDuration}ms;
  transition-timing-function: ${transitionTimingFn};

  @media (min-width: ${media.phone}) {
    border-radius: 0 0 4rem 0;
  }

  &:hover,
  &:active,
  &:focus {
    cursor: pointer;
    color: ${colors.graphite};
    transform: translateY(-0.6rem);
    box-shadow: 0px 6px 15px 5px rgba(0, 0, 0, 0.3);
  }

  img {
    width: 100%;
  }

  ${T1M} {
    margin-top: auto;
    padding: 0 1.4rem 1.4rem 1.4rem;

    @media (min-width: ${media.phone}) {
      padding: 0 1.7rem 1.7rem 1.7rem;
    }
  }

  p {
    margin-top: 0.4rem;

    ${props =>
      (typeof props.children !== 'undefined' ||
        typeof props.tags !== 'undefined' ||
        typeof props.action !== 'undefined') &&
      css`
        margin-bottom: 1.4rem;

        @media (min-width: ${media.phone}) {
          margin-bottom: 1.7rem;
        }
      `}
  }

  p,
  span,
  h1,
  h2,
  h3,
  h4,
  h5,
  h6 {
    word-break: break-word;
  }
`;

const ChildrenContainer = styled.div`
  padding: 0 1.4rem 1.4rem 1.4rem;

  @media (min-width: ${media.phone}) {
    padding: 0 1.7rem 1.7rem 1.7rem;
  }
`;

const ButtonContainer = styled.div`
  margin: auto auto 1.4rem 1.4rem;

  @media (min-width: ${media.phone}) {
    margin: auto auto 1.7rem 1.7rem;
  }

  ${Button} {
    text-align: center;
  }
`;

const CardContentContainer = styled.div`
  padding: 1.4rem 1.4rem 0 1.4rem;
  display: flex;
  flex-direction: column;
  justify-content: space-between;

  @media (min-width: ${media.phone}) {
    padding: 1.7rem 1.7rem 0 1.7rem;
  }
`;

const DateLocationContainer = styled.address`
  display: flex;
  flex-direction: column;
  margin: 1.5rem 0;
`;

const displayTags = tags => {
  const formattedTags = tags.map(t => (typeof t === 'object' ? t.name : t));
  return formattedTags.join(', ');
};

const getCardProps = link => {
  return link ? getLinkProps(link) : {};
};

class Card extends PureComponent {
  static propTypes = {
    imgSrc: PropTypes.oneOfType([PropTypes.object, PropTypes.string]),
    imgAlt: PropTypes.string,
    imageLoading: PropTypes.string,
    title: PropTypes.string.isRequired,
    date: PropTypes.string,
    location: PropTypes.string,
    text: PropTypes.string.isRequired,
    tags: PropTypes.array,
    link: PropTypes.string,
    action: PropTypes.string,
    children: PropTypes.oneOfType([
      PropTypes.arrayOf(PropTypes.node),
      PropTypes.node
    ])
  };

  handleClick = () => navigate(this.props.link);

  render() {
    const {
      link,
      imgSrc,
      imgAlt,
      imageLoading,
      title,
      date,
      location,
      text,
      action,
      tags,
      children
    } = this.props;

    return (
      <StyledCard {...getCardProps(link)}>
        {imgSrc && (
          <div>
            {imgSrc.childImageSharp ? (
              <Img
                style={{ overflow: 'unset' }}
                fluid={imgSrc.childImageSharp.fluid}
                fixed={imgSrc.childImageSharp.fixed}
                alt={imgAlt}
                loading={imageLoading || 'lazy'}
              />
            ) : (
              <img src={imgSrc.publicURL} alt={imgAlt} />
            )}
          </div>
        )}
        <CardContentContainer>
          <div>
            <T2B color={colors.cyan}>{title}</T2B>
            {date && location && (
              <DateLocationContainer>
                <T2R>{`${format(date, 'MMMM D')} at ${format(
                  date,
                  'h:mma'
                )}`}</T2R>
                <T2R>{location}</T2R>
              </DateLocationContainer>
            )}
            {text && <T2R as="p">{text}</T2R>}
          </div>
        </CardContentContainer>
        {children && <ChildrenContainer>{children}</ChildrenContainer>}
        {action && (
          <ButtonContainer>
            <Button type="secondary">{action}</Button>
          </ButtonContainer>
        )}
        {tags && <T1M color={colors.cyan}>{displayTags(tags)}</T1M>}
      </StyledCard>
    );
  }
}

export default Card;
